import {
	getContactWithOrders,
	saveConsultantContact,
} from '@/API/repositories/contact';
import { getProductByUserQueue } from '@/API/repositories/product';
import { saveInboundCall } from '@/API/repositories/queue';
import { createSuspicionTicket } from '@/API/repositories/tickets';
import { Colors } from '@/common/colors/colors';
import AdditionalOrderCreate from '@/common/components/additionalOrderCreate/AdditionalOrderCreate';
import ActionButton from '@/common/components/buttons/ActionButton';
import Card from '@/common/components/Card';
import Loading from '@/common/components/Loading';
import PopUp from '@/common/components/PopUp';
import Table from '@/common/components/Table';
import MailsTimeline from '@/common/components/timelines/mailsTimeline/MailsTimeline';
import ShippingTimeline from '@/common/components/timelines/shippingTimeline/ShippingTimeline';
import { checkOnTheWay } from '@/common/functions/checkOnTheWay';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import OrderEditInbound from '@/pages/consultant/consultantContactInbound/components/orderEditInbound/OrderEditInbound';
import Comments from '@/pages/consultant/contact/comments/Comments';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import ContactForm from './components/contactForm/ContactForm';
import { HEADERS } from './constants/headers';
import {
	AddOrdersWrapper,
	CardWrapper,
	ContactTableWrapper,
	PromotionResultTitle,
	Wrapper,
} from './ConsultantContactInbound.styled';
import { getRaws } from './helpers/getRaws';
import {
	handleCheckAdditionalOrder,
	handleCheckAddOrder,
	isCzechWithoutPromotion,
} from './helpers/orderCheckers';
import { useResignOrder } from './hooks/useResignOrder';
import Resign from '@/common/components/resign/Resign';
import { markOrderAsResigned } from '@/API/repositories/order';
import { useAddressPopup } from '../contact/addressPopup/hooks/useAddressPopup';
import AddressPopup from '../contact/addressPopup/AddressPopup';
import ContactFormAddressValidation from './components/contactForm/ContactFormAddressValidation';
import { MARKETS_TO_VALIDATE_ADDRESS } from '../consultantContactOutbound/constants/marketsToValidateAddress';
import AddressValidationOrderEditInbound from './components/orderEditInbound/AddressValidationOrderEditInbound';
import { AuthContext } from '@/common/contexts/authContext';


const ConsultantContactInbound = () => {
	const { authUser } = useContext(AuthContext);

	const [country, setCountry] = useState();
	const [create, setCreate] = useState(false);
	const [dataToDisplay, setDataToDisplay] = useState(null);
	const [promotionResult, setPromotionResult] = useState();
	const [mailsTimeline, setMailsTimeline] = useState();
	const [shippingTimeline, setShippingTimeline] = useState();
	const [showAdditionalOrderCreate, setShowAdditionalOrderCreate] = useState();

	const [show, setShow] = useState(false);
	const [order, setOrder] = useState({});

	const onContactUpdate = (contact) => {
		setDataToDisplay((prev) => {
			return { ...prev, ...contact };
		});
	};

	const addressPopupHook = useAddressPopup(dataToDisplay, onContactUpdate);

	const user = authUser;

	const { id } = useParams();
	const navigate = useNavigate();
	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const { COUNTRIES } = useCommonDataContext();
	const {
		commonData: { markets },
	} = useCommonDataContext();

	const market = useMemo(() => {
		return markets.find((m) => m.short === dataToDisplay?.country);
	}, [dataToDisplay]);

	const canEnd = useMemo(() => {
		const threshold = moment().subtract(20, 'minutes').utc();
		let isCommentAddedByUser =
			dataToDisplay?._comments.find((c) => {
				const commentTimeUTC = moment(c.created_at).utc();
				return c.user === user.id && threshold.isBefore(commentTimeUTC);
			}) || false;

		return !!isCommentAddedByUser;
	}, [dataToDisplay, user.id]);

	const handleEndInboundContact = async (e) => {
		e.preventDefault();

		const response = await makeRequest(saveInboundCall);

		if (response.data) {
			navigate('/consultant/dashboard');
		}
	};

	const handleShowOrderEdit = (e, data, create) => {
		e.preventDefault();
		setCreate(() => create);
		setOrder(() => data);
		setShow(() => true);
	};

	const handleGetContactWithOrders = async () => {
		const response = await makeRequest(getContactWithOrders.bind(null, id));
		const responseProduct = await makeRequest(
			getProductByUserQueue.bind(null, authUser.id),
		);

		if (response.data && responseProduct.data) {
			setCountry(
				COUNTRIES.find((country) => country.value === response.data.country),
			);

			setDataToDisplay(() => null);
			setDataToDisplay(() => response.data);
		} else {
			setDataToDisplay(() => null);
		}
	};

	const {
		handleResignConfirm,
		setShowResignConfirmationPopup,
		showResignConfirmationPopup,
		handleResignButtonClick,
	} = useResignOrder(handleGetContactWithOrders);

	useEffect(() => {
		handleGetContactWithOrders();
	}, [show]);

	return (
		<Wrapper>
			{hasUnfilledRequest(
				saveConsultantContact,
				getContactWithOrders,
				createSuspicionTicket,
				markOrderAsResigned,
			) && <Loading />}
			{dataToDisplay && (
				<CardWrapper>
					<ActionButton
						onClick={(e) => handleEndInboundContact(e)}
						disabled={!canEnd}
					>
						{canEnd
							? ' End Inbound Contact '
							: 'Please add a comment to end a contact'}
					</ActionButton>
					{MARKETS_TO_VALIDATE_ADDRESS.includes(dataToDisplay?.country) && (
						<ContactFormAddressValidation
							dataToDisplay={dataToDisplay}
							country={country}
							setCountry={setCountry}
							reload={handleGetContactWithOrders}
							setPromotionResult={setPromotionResult}
							onEditAddress={addressPopupHook.openContactAddressPopup}
						/>
					)}
					{!MARKETS_TO_VALIDATE_ADDRESS.includes(dataToDisplay?.country) && (
						<ContactForm
							dataToDisplay={dataToDisplay}
							country={country}
							setCountry={setCountry}
							reload={handleGetContactWithOrders}
							setPromotionResult={setPromotionResult}
						/>
					)}

					<Card>
						<div style={{ width: '1000px' }}>
							<h3 style={{ color: Colors.darkBlue, marginBottom: '30px' }}>
								Orders:
							</h3>
							<ContactTableWrapper>
								<Table
									className='styled-table'
									headers={HEADERS}
									raws={getRaws({
										dataToDisplay,
										setMailsTimeline,
										setShippingTimeline,
										handleShowOrderEdit,
										handleResignButtonClick,
									})}
								/>
							</ContactTableWrapper>
						</div>
						<AddOrdersWrapper>
							{handleCheckAdditionalOrder() && (
								<ActionButton
									onClick={() => setShowAdditionalOrderCreate(true)}
									disabled={isCzechWithoutPromotion()}
									defaultText='Add additional order'
								/>
							)}
							{handleCheckAddOrder() && (
								<ActionButton
									onClick={(e) => handleShowOrderEdit(e, {}, true)}
									disabled={isCzechWithoutPromotion()}
									defaultText='Add Order'
								/>
							)}
						</AddOrdersWrapper>
					</Card>
					{dataToDisplay && (
						<Card>
							<div style={{ width: '1000px' }}>
								<h3 style={{ color: Colors.darkBlue }}>Comments:</h3>
								<Comments
									comments={dataToDisplay?._comments || []}
									contactId={id}
									handleGetDawnContact={() => handleGetContactWithOrders()}
								/>
							</div>
						</Card>
					)}
					{promotionResult && (
						<PopUp setShow={setPromotionResult}>
							<PromotionResultTitle isGood={promotionResult.isGood}>
								{promotionResult.message}
							</PromotionResultTitle>
						</PopUp>
					)}
				</CardWrapper>
			)}
			{show && (
				<>
					{MARKETS_TO_VALIDATE_ADDRESS.includes(dataToDisplay?.country) ? (
						<AddressValidationOrderEditInbound
							comments={dataToDisplay?._comments}
							inbound={true}
							order={order}
							setShow={setShow}
							setOrder={setOrder}
							contact={dataToDisplay}
							country={country}
							market={market}
							create={create}
							onEditAddress={addressPopupHook.openContactAddressPopup}
						/>
					) : (
						<OrderEditInbound
							comments={dataToDisplay?._comments}
							inbound={true}
							order={order}
							setShow={setShow}
							setOrder={setOrder}
							contact={dataToDisplay}
							country={country}
							market={market}
							create={create}
						/>
					)}
				</>
			)}
			{showAdditionalOrderCreate && (
				<AdditionalOrderCreate
					setShow={setShowAdditionalOrderCreate}
					orderById={
						dataToDisplay?.orders?.find((order) => checkOnTheWay(order))?._id
					}
					reloadFn={handleGetContactWithOrders}
				/>
			)}
			{mailsTimeline?.length && (
				<MailsTimeline setShow={setMailsTimeline} mails={mailsTimeline} />
			)}
			{shippingTimeline && (
				<ShippingTimeline
					setShow={setShippingTimeline}
					shipping={shippingTimeline}
				/>
			)}
			{showResignConfirmationPopup && (
				<Resign
					setShow={setShowResignConfirmationPopup}
					handleResign={handleResignConfirm}
				/>
			)}
			{addressPopupHook.isAddressPopupOpen && (
				<AddressPopup contact={dataToDisplay} hook={addressPopupHook} />
			)}
		</Wrapper>
	);
};

export default ConsultantContactInbound;
